import { type FormikValues, useFormikContext } from 'formik';
import React, { type ChangeEvent, type TextareaHTMLAttributes, useEffect } from 'react';

import { EStyles } from '@enums/style.enum';

import { ErrorMessage } from '../index';
import classes from './FormTextarea.module.scss';

interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
	firstValue?: unknown;
	name: string;
	label?: string;
	placeholder?: string;
	rows?: number;
	maxLength?: number;
	onChangeCallBack?: (name: string, text: string) => void;
	displayError?: boolean;
}

const FormTextarea: React.FC<Props> = ({
	firstValue,
	name,
	label,
	placeholder,
	rows = 4,
	maxLength,
	onChangeCallBack,
	id,
	displayError = true,
	...otherProps
}: React.PropsWithChildren<Props>) => {
	const { setFieldTouched, setFieldValue, errors, touched, values } = useFormikContext<FormikValues>();

	const isError = !!touched[name] && !!errors[name];

	const handleFirstValue = (firstValue: unknown) => {
		setFieldValue(name, firstValue);
	};

	const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		onChangeCallBack?.(name, e.target.value);
		setFieldValue(name, e.target.value);
	};

	useEffect(() => {
		handleFirstValue(firstValue);
	}, [firstValue]);

	return (
		<div className={classes['container']}>
			{label && (
				<label htmlFor={id} className={classes['label']}>
					{label}
				</label>
			)}
			<textarea
				id={id}
				className={`${classes['textarea']} ${isError ? classes['error'] : EStyles.emptyQuote}`}
				placeholder={placeholder}
				rows={rows}
				maxLength={maxLength}
				value={(values[name] as string) || EStyles.emptyQuote}
				onChange={handleChange}
				onBlur={() => setFieldTouched(name)}
				autoCorrect={EStyles.off}
				spellCheck={EStyles.false}
				{...otherProps}
			/>
			{displayError && <ErrorMessage error={errors[name] as string | undefined} visible={isError} />}
		</div>
	);
};

export default FormTextarea;
